(() => {
  const QUESTION_LABELS = {
    recipient: {
      self: 'Para mí',
      family: 'Para mi familia',
      pregnancy: 'Para embarazo o lactancia',
      older_person: 'Para una persona mayor',
      unsure: 'Aún no estoy seguro'
    },
    goal: {
      choose_omega3: 'Elegir mejor un omega-3',
      daily_habit: 'Crear un hábito diario',
      practical: 'Encontrar una opción práctica',
      family_purchase: 'Comprar para la familia',
      compare: 'Comparar antes de decidir'
    },
    format: {
      liquid: 'Líquido',
      capsules: 'Cápsulas',
      unsure: 'No estoy seguro'
    },
    routine: {
      home: 'En casa',
      on_the_go: 'En movimiento',
      family_routine: 'En una rutina familiar',
      established_routine: 'En una rutina ya establecida',
      unsure: 'Todavía no sé'
    },
    purchase_channel: {
      online: 'En línea, en el sitio Lýsi',
      physical_store: 'En tienda física',
      marketplace: 'En marketplace o retailer'
    },
    familiarity: {
      beginner: 'Estoy empezando',
      used_before: 'Ya lo he tomado antes',
      buying_for_other: 'Lo compro para alguien más',
      learn_first: 'Quiero entender mejor antes de elegir'
    },
    marketing_consent: {
      yes: 'Sí, quiero recibirlas',
      no: 'No, gracias'
    }
  };

  const RESULT_TITLES = {
    A: 'Mejor para integrar en casa',
    B: 'Mejor para practicidad',
    C: 'Mejor para familia',
    D: 'Mejor para comparar antes de comprar',
    E: 'Consulta antes de elegir'
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  function createSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `lysi-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function readUtm() {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      content: params.get('utm_content') || '',
      term: params.get('utm_term') || ''
    };
  }

  function initQuiz(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const configElement = root.querySelector('[data-lysi-quiz-config]');
    if (!configElement) return;

    let config;
    try {
      config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('No se pudo leer la configuración del quiz Lýsi.', error);
      return;
    }

    const steps = Array.from(root.querySelectorAll('[data-step]'));
    const questionSteps = steps.filter((step) => step.dataset.step !== 'result');
    const resultStep = root.querySelector('[data-step="result"]');
    const resultPanels = Array.from(root.querySelectorAll('[data-result]'));
    const previousButton = root.querySelector('[data-previous]');
    const nextButton = root.querySelector('[data-next]');
    const navigation = root.querySelector('[data-quiz-navigation]');
    const progressWrap = root.querySelector('[data-quiz-progress-wrap]');
    const progressBar = root.querySelector('[data-progress-bar]');
    const progressLabel = root.querySelector('[data-progress-label]');
    const progressPercent = root.querySelector('[data-progress-percent]');
    const status = root.querySelector('[data-quiz-status]');
    const nameInput = root.querySelector('[data-quiz-name]');
    const nameError = root.querySelector('[data-name-error]');
    const emailInput = root.querySelector('[data-quiz-email]');
    const emailError = root.querySelector('[data-email-error]');
    const honeypot = root.querySelector('[data-honeypot]');
    const purchaseNote = root.querySelector('[data-purchase-note]');
    const restartButton = root.querySelector('[data-restart-quiz]');
    const subscribeForm = document.getElementById(config.subscribeFormId);
    const subscribeName = subscribeForm?.querySelector('[data-subscribe-name]');
    const subscribeEmail = subscribeForm?.querySelector('[data-subscribe-email]');

    const storageKey = `lysi_quiz_session_${config.sectionId}`;
    const state = {
      currentStep: 1,
      answers: {},
      name: '',
      email: '',
      resultCode: null,
      started: false,
      completed: false,
      startedAt: new Date().toISOString(),
      startedAtMs: Date.now(),
      sessionId: sessionStorage.getItem(storageKey) || createSessionId(),
      sentCheckpoints: new Set(),
      lastExitSentAt: 0
    };

    sessionStorage.setItem(storageKey, state.sessionId);

    function setStatus(message = '') {
      status.textContent = message;
    }

    function getCurrentStepElement() {
      return root.querySelector(`[data-step="${state.currentStep}"]`);
    }

    function answerObject() {
      return Object.fromEntries(
        Object.entries(state.answers).map(([key, value]) => [
          key,
          {
            value,
            label: QUESTION_LABELS[key]?.[value] || value
          }
        ])
      );
    }

    function collectRecommendedProducts() {
      if (!state.resultCode) return [];
      const panel = root.querySelector(`[data-result="${state.resultCode}"]`);
      if (!panel) return [];
      return Array.from(panel.querySelectorAll('[data-product-handle]'))
        .map((card) => card.dataset.productHandle)
        .filter(Boolean);
    }

    function spamFlags() {
      const flags = [];
      if (honeypot?.value) flags.push('honeypot_filled');
      if (Date.now() - state.startedAtMs < Number(config.minimumCompletionMs || 8000)) {
        flags.push('completed_too_fast');
      }
      return flags;
    }

    function buildPayload(eventName, extra = {}) {
      return {
        source_token: config.sourceToken,
        schema_version: config.schemaVersion,
        event: eventName,
        quiz_id: config.quizId,
        session_id: state.sessionId,
        status: state.completed ? 'completed' : 'in_progress',
        current_step: state.currentStep,
        total_steps: questionSteps.length,
        started_at: state.startedAt,
        last_activity_at: new Date().toISOString(),
        completed_at: state.completed ? new Date().toISOString() : '',
        name: state.name,
        email: state.email,
        marketing_consent:
          state.answers.marketing_consent === 'yes'
            ? true
            : state.answers.marketing_consent === 'no'
              ? false
              : null,
        result_code: state.resultCode,
        result_title: state.resultCode ? RESULT_TITLES[state.resultCode] : '',
        recommended_product_handles: collectRecommendedProducts(),
        answers: answerObject(),
        page_url: window.location.href,
        referrer: document.referrer || '',
        utm: readUtm(),
        spam_flags: eventName === 'quiz_completed' ? spamFlags() : [],
        ...extra
      };
    }

    async function sendEvent(eventName, extra = {}, options = {}) {
      if (!config.webhookUrl || !config.sourceToken) return false;
      if (honeypot?.value) return false;

      try {
        const response = await fetch(config.webhookUrl, {
          method: 'POST',
          mode: 'cors',
          keepalive: Boolean(options.keepalive),
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildPayload(eventName, extra))
        });

        return response.ok;
      } catch (error) {
        console.warn(`No se pudo enviar el evento ${eventName} a Make.`, error);
        return false;
      }
    }

    function markQuizStarted() {
      if (state.started) return;
      state.started = true;
      sendEvent('quiz_started');
    }

    function maybeSendCheckpoint(stepNumber) {
      const checkpointSteps = [3, 6];
      if (!checkpointSteps.includes(stepNumber)) return;
      if (state.sentCheckpoints.has(stepNumber)) return;
      state.sentCheckpoints.add(stepNumber);
      sendEvent('quiz_checkpoint', { checkpoint_step: stepNumber });
    }

    function updateProgress() {
      const percent = Math.round((state.currentStep / questionSteps.length) * 100);
      progressLabel.textContent = `Pregunta ${state.currentStep} de ${questionSteps.length}`;
      progressPercent.textContent = `${percent}%`;
      progressBar.style.width = `${percent}%`;
    }

    function updateNavigation() {
      previousButton.disabled = state.currentStep === 1;

      const current = getCurrentStepElement();
      const questionKey = current?.dataset.question;
      let canContinue = Boolean(questionKey && state.answers[questionKey]);

      if (questionKey === 'email') {
        const name = nameInput.value.trim();
        const email = emailInput.value.trim();
        canContinue = name.length >= 2 && isValidEmail(email);
      }

      nextButton.disabled = !canContinue;
      nextButton.textContent = state.currentStep === questionSteps.length ? 'Ver resultado' : 'Siguiente';
    }

    function showStep(stepNumber) {
      state.currentStep = stepNumber;
      questionSteps.forEach((step) => {
        const active = Number(step.dataset.step) === stepNumber;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });

      resultStep.hidden = true;
      navigation.hidden = false;
      progressWrap.hidden = false;
      updateProgress();
      updateNavigation();
      setStatus('');

      const current = getCurrentStepElement();
      current?.querySelector('h2, input, button')?.focus({ preventScroll: true });
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function setAnswer(step, value, button) {
      const key = step.dataset.question;
      if (!key) return;

      state.answers[key] = value;
      step.querySelectorAll('[data-value]').forEach((option) => {
        const selected = option === button;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      markQuizStarted();
      updateNavigation();
    }

    function calculateResult() {
      const answers = state.answers;

      if (answers.recipient === 'pregnancy') return 'E';

      const familySignal =
        answers.recipient === 'family' ||
        answers.goal === 'family_purchase' ||
        answers.routine === 'family_routine';

      if (familySignal) return 'C';

      const scores = { A: 0, B: 0, D: 0 };

      if (answers.recipient === 'self') {
        scores.A += 1;
        scores.B += 1;
      }

      if (answers.recipient === 'older_person') {
        scores.A += 1;
        scores.B += 1;
      }

      if (answers.recipient === 'unsure') scores.D += 1;

      if (answers.goal === 'choose_omega3') {
        scores.A += 1;
        scores.B += 1;
      }
      if (answers.goal === 'daily_habit') {
        scores.A += 1;
        scores.B += 1;
      }
      if (answers.goal === 'practical') scores.B += 3;
      if (answers.goal === 'compare') scores.D += 4;

      if (answers.format === 'liquid') scores.A += 4;
      if (answers.format === 'capsules') scores.B += 4;
      if (answers.format === 'unsure') scores.D += 1;

      if (answers.routine === 'home') scores.A += 3;
      if (answers.routine === 'on_the_go') scores.B += 3;
      if (answers.routine === 'established_routine') {
        scores.A += 1;
        scores.B += 1;
      }
      if (answers.routine === 'unsure') scores.D += 1;

      // “Estoy empezando” funciona como contexto editorial, no como regla de resultado.
      if (answers.familiarity === 'buying_for_other') scores.D += 1;
      if (answers.familiarity === 'learn_first') scores.D += 4;

      const strongestProductScore = Math.max(scores.A, scores.B);
      if (scores.D >= 5 && scores.D > strongestProductScore) return 'D';
      if (strongestProductScore < 3 && scores.D >= 2) return 'D';

      if (scores.A === scores.B) {
        if (answers.format === 'capsules' || answers.routine === 'on_the_go') return 'B';
        if (answers.format === 'liquid' || answers.routine === 'home') return 'A';
        return 'D';
      }

      return scores.B > scores.A ? 'B' : 'A';
    }

    function updatePurchaseNote() {
      const messages = {
        online: 'Seleccionaste compra en línea. Puedes revisar las fichas de los productos recomendados.',
        physical_store: 'Seleccionaste tienda física. Usa “Dónde comprar” para revisar puntos de venta.',
        marketplace: 'Seleccionaste marketplace o retailer. Usa “Dónde comprar” para revisar opciones disponibles.'
      };
      purchaseNote.textContent = messages[state.answers.purchase_channel] || '';
      purchaseNote.hidden = !purchaseNote.textContent;
    }

    async function subscribeCustomer() {
      if (state.answers.marketing_consent !== 'yes') return 'not_requested';
      if (!subscribeForm || !subscribeEmail) return 'form_missing';

      if (subscribeName) subscribeName.value = state.name;
      subscribeEmail.value = state.email;

      try {
        const response = await fetch(subscribeForm.action, {
          method: 'POST',
          body: new FormData(subscribeForm),
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        return response.ok ? 'submitted' : 'failed';
      } catch (error) {
        console.warn('No se pudo registrar la suscripción en Shopify.', error);
        return 'failed';
      }
    }

    async function completeQuiz() {
      state.resultCode = calculateResult();
      state.completed = true;

      questionSteps.forEach((step) => {
        step.hidden = true;
      });
      resultPanels.forEach((panel) => {
        panel.hidden = panel.dataset.result !== state.resultCode;
      });

      resultStep.hidden = false;
      navigation.hidden = true;
      progressWrap.hidden = true;
      updatePurchaseNote();
      setStatus('');
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const subscriptionStatus = await subscribeCustomer();
      await sendEvent('quiz_completed', {
        shopify_subscription_status: subscriptionStatus,
        experience_level: state.answers.familiarity === 'beginner' ? 'beginner' : 'not_beginner'
      });
    }

    async function goNext() {
      const current = getCurrentStepElement();
      const questionKey = current?.dataset.question;
      if (!questionKey) return;

      if (questionKey === 'email') {
        const name = nameInput.value.trim();
        const email = emailInput.value.trim().toLowerCase();

        if (name.length < 2) {
          nameInput.setAttribute('aria-invalid', 'true');
          nameError.textContent = 'Ingresa tu nombre.';
          nameInput.focus();
          return;
        }

        if (!isValidEmail(email)) {
          emailInput.setAttribute('aria-invalid', 'true');
          emailError.textContent = 'Ingresa un correo electrónico válido.';
          emailInput.focus();
          return;
        }

        nameInput.removeAttribute('aria-invalid');
        nameError.textContent = '';
        emailInput.removeAttribute('aria-invalid');
        emailError.textContent = '';
        state.name = name;
        state.email = email;
        state.answers.name = name;
        state.answers.email = email;
        await sendEvent('email_captured');
      }

      if (state.currentStep === questionSteps.length) {
        await completeQuiz();
        return;
      }

      maybeSendCheckpoint(state.currentStep);
      showStep(state.currentStep + 1);
    }

    function goPrevious() {
      if (state.currentStep <= 1) return;
      showStep(state.currentStep - 1);
    }

    function resetQuiz() {
      sessionStorage.removeItem(storageKey);
      window.location.reload();
    }

    root.querySelectorAll('[data-question]').forEach((step) => {
      step.querySelectorAll('[data-value]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => setAnswer(step, button.dataset.value, button));
      });
    });

    nameInput.addEventListener('input', () => {
      nameInput.removeAttribute('aria-invalid');
      nameError.textContent = '';
      updateNavigation();
    });

    emailInput.addEventListener('input', () => {
      emailInput.removeAttribute('aria-invalid');
      emailError.textContent = '';
      updateNavigation();
    });

    nextButton.addEventListener('click', goNext);
    previousButton.addEventListener('click', goPrevious);
    restartButton.addEventListener('click', resetQuiz);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') return;
      if (!state.started || state.completed) return;
      if (Date.now() - state.lastExitSentAt < 30000) return;

      state.lastExitSentAt = Date.now();
      sendEvent('quiz_exit', { exit_reason: 'page_hidden' }, { keepalive: true });
    });

    updateProgress();
    updateNavigation();
  }

  function initAll() {
    document.querySelectorAll('[data-lysi-quiz]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-lysi-quiz]').forEach(initQuiz);
    if (event.target.matches?.('[data-lysi-quiz]')) initQuiz(event.target);
  });
})();
